import { IconProp, SizeProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import React, { Children, MouseEventHandler, useEffect } from "react";
import {
  faChevronRight,
  faEllipsis,
  faChevronLeft,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/pro-regular-svg-icons";
import useTooltip from "../../hooks/useTooltip";
import { useBottomSheet } from "@/components/shared/hooks/mobile/useBottomSheet.tsx";
import BottomSheet from "@/components/shared/mobile/BottomSheet.tsx";

export function closeAllDropdowns() {
  const allDropdowns = document.querySelectorAll(".tixxt__dropdown");
  allDropdowns.forEach((dropdown) => {
    dropdown.classList.remove("show");
  });
}

type DropDownArgs = {
  children: React.ReactNode;
  alignment?: "start" | "end";
  className?: string;
};

type DropdownContextValue = {
  onItemSelect?: () => void;
};

export const DropdownContext = React.createContext<DropdownContextValue | null>(
  null,
);

function DropDown({ children, className, alignment = "end" }: DropDownArgs) {
  // make sure to close dropdowns on body click
  useEffect(() => {
    document.body.addEventListener("click", closeAllDropdowns);

    return () => {
      document.body.removeEventListener("click", closeAllDropdowns);
    };
  }, []);

  return (
    <ul
      role="menu"
      className={classNames(
        "tixxt__dropdown",
        !window.isApp &&
          `dropdown-menu dropdown-menu-${alignment} empty:hidden border-box`,
        className,
      )}
    >
      {children}
    </ul>
  );
}

export function DropDownSubMenu(props: Omit<DropDownNestedMenuArgs, "level">) {
  return <DropDownNestedMenu {...props} level={1} />;
}

export function DropDownSubSubMenu(
  props: Omit<DropDownNestedMenuArgs, "level">,
) {
  return <DropDownNestedMenu {...props} level={2} />;
}

type DropDownItemArgs = {
  children?: React.ReactNode;
  url?: string;
  icon?: IconProp;
  text?: string;
  additional?: React.ReactNode;
  linkStyle?: string;
  iconStyle?: React.CSSProperties;
  method?: string;
  confirm?: string;
  className?: string;
  iconClassName?: string;
  onSelect?: MouseEventHandler<HTMLAnchorElement>;
  title?: string;
  dividerTop?: boolean;
  dividerBottom?: boolean;
  target?: string | null;
  data?: { [key: string]: string | boolean };
  propagateClick?: boolean;
};

export function DropDownItem({
  children,
  url,
  icon,
  text,
  additional,
  linkStyle,
  iconStyle,
  method,
  confirm,
  className,
  iconClassName,
  onSelect,
  title,
  dividerTop,
  dividerBottom,
  target,
  data,
  propagateClick = false,
}: DropDownItemArgs) {
  const dropdownContext = React.useContext(DropdownContext);
  const ItemWrapperComponent = ItemWrapper;
  return (
    <>
      {dividerTop && <li className="divider" />}
      <ItemWrapperComponent className={className} title={title}>
        {children ? (
          children
        ) : (
          <a
            href={url || "#"}
            className={classNames("dropdown-item", linkStyle)}
            role="menuitem"
            data-method={method}
            data-confirm={confirm}
            onClick={(e) => {
              if (onSelect) {
                if (!propagateClick) e.stopPropagation();
                e.preventDefault();
                onSelect(e);
                dropdownContext?.onItemSelect?.();
              }
            }}
            target={target || undefined}
            {...data}
          >
            <span className="flex items-center gap-1">
              {icon && (
                <FontAwesomeIcon
                  icon={icon}
                  className={classNames("fa-fw", iconClassName)}
                  style={iconStyle}
                />
              )}
              {text && <span className="flex-1">{text}</span>}
              {additional}
            </span>
          </a>
        )}
      </ItemWrapperComponent>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type ItemWrapperProps = {
  className?: string;
  title?: string;
  children: React.ReactNode;
};

function ItemWrapper({ className, title, children }: ItemWrapperProps) {
  const tooltipRef = useTooltip<HTMLLIElement>(title);
  return (
    <li ref={tooltipRef} className={className} title={title}>
      {children}
    </li>
  );
}
type DropDownNestedMenuArgs = {
  text: string;
  children: React.ReactNode;
  className?: string;
  icon?: IconProp;
  iconClassName?: string;
  side?: "left" | "right" | "bottom";
  dividerTop?: boolean;
  dividerBottom?: boolean;
  level: number;
};

// Would a submenu placed to the right of its item reach past the right edge of
// the window? Kept apart from the measuring so the decision itself is testable:
// in jsdom every box is zero wide, so going through the DOM proves nothing.
export const SUBMENU_GAP = 4;

export function submenuLeavesViewport({
  itemRight,
  submenuWidth,
  viewportWidth,
  gap = SUBMENU_GAP,
}: {
  itemRight: number;
  submenuWidth: number;
  viewportWidth: number;
  gap?: number;
}) {
  return itemRight + gap + submenuWidth > viewportWidth;
}

function DropDownNestedMenu({
  text,
  children,
  className,
  icon,
  iconClassName,
  side: preferredSide = "right",
  dividerTop,
  dividerBottom,
  level,
}: DropDownNestedMenuArgs) {
  const submenuRef = React.useRef<HTMLUListElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const [flipped, setFlipped] = React.useState(false);
  const side = preferredSide === "right" && flipped ? "left" : preferredSide;
  const [showSubmenu, setShowSubmenu] = React.useState(
    !!submenuRef?.current?.classList.contains("show"),
  );
  const levelClass = `level-${level}`;

  useEffect(() => {
    if (!submenuRef.current) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          setShowSubmenu(!!submenuRef?.current?.classList.contains("show"));
        }
      });
    });

    observer.observe(submenuRef.current, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // A submenu meant for the right opens to the left when it would leave the
  // viewport there, e.g. in a context menu at the right edge of the page.
  // Measured once it is visible, as its width depends on the entries.
  function placeSubmenu() {
    if (preferredSide !== "right" || window.isApp) return;
    requestAnimationFrame(() => {
      const submenu = submenuRef.current;
      const item = submenu?.parentElement;
      if (!submenu || !item || !submenu.offsetWidth) return;
      // Unfolded below the item (phones): nothing to place
      if (getComputedStyle(submenu).position === "static") return;
      setFlipped(
        submenuLeavesViewport({
          itemRight: item.getBoundingClientRect().right,
          submenuWidth: submenu.offsetWidth,
          viewportWidth: document.documentElement.clientWidth,
        }),
      );
    });
  }

  function toggleSubmenu() {
    if (showSubmenu) {
      submenuRef?.current?.classList.add("hidden");
      submenuRef.current?.classList.remove("show");
    } else {
      submenuRef?.current?.classList.add("show");
      submenuRef.current?.classList.remove("hidden");
      placeSubmenu();
    }
  }

  return (
    <>
      {dividerTop && <li className="divider" />}
      <li
        className={classNames(`relative group/${levelClass}`, className)}
        onMouseEnter={placeSubmenu}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleSubmenu();
        }}
        // Closing sits on the item, not on its first line: once the submenu is
        // open the focus is inside it, out of reach of the line above
        onKeyDown={(e) => {
          if ((e.key === "Escape" || e.key === "ArrowLeft") && showSubmenu) {
            e.preventDefault();
            e.stopPropagation();
            toggleSubmenu();
            triggerRef.current?.focus();
          }
        }}
      >
        <div
          ref={triggerRef}
          className="dropdown-item flex gap-2 items-center w-full text-left cursor-pointer"
          role="menuitem"
          aria-haspopup="menu"
          aria-expanded={showSubmenu}
          // Reachable without a mouse: the entries themselves are hidden until
          // the submenu is open, so the keyboard needs this one to get in
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
              e.preventDefault();
              e.stopPropagation();
              if (!showSubmenu) toggleSubmenu();
              // let the first entry take the focus
              requestAnimationFrame(() =>
                submenuRef.current
                  ?.querySelector<HTMLElement>("a, button")
                  ?.focus(),
              );
            }
          }}
        >
          <span className="flex items-center space-x-1">
            {side === "left" && !window.isApp && (
              <FontAwesomeIcon icon={faChevronLeft} className="ml-auto" />
            )}
            {icon && (
              <FontAwesomeIcon
                icon={icon}
                className={classNames("fa-fw", iconClassName)}
              />
            )}
            <span className="flex-1">{text}</span>
            {level === 2 &&
              (side === "right" || side === "bottom" || window.isApp) && (
                <FontAwesomeIcon
                  icon={
                    window.isApp
                      ? showSubmenu
                        ? faChevronUp
                        : faChevronDown
                      : faChevronRight
                  }
                  className="ml-auto"
                />
              )}
          </span>
          {level === 1 &&
            (side === "right" || side === "bottom" || window.isApp) && (
              <FontAwesomeIcon
                icon={
                  window.isApp
                    ? showSubmenu
                      ? faChevronUp
                      : faChevronDown
                    : faChevronRight
                }
                className="ml-auto"
              />
            )}
        </div>
        <ul
          ref={submenuRef}
          role="menu"
          className={classNames("tixxt__dropdown", {
            // Open and closed belong in the class React renders itself: the
            // class was set on the element directly before, and every render
            // that changed the side (see placeSubmenu) wrote it away again,
            // which closed the submenu right after it had opened
            hidden: !showSubmenu,
            show: showSubmenu,
            "dropdown-menu absolute top-0 mt-0": !window.isApp,
            // A phone has no room beside the menu: unfold below the item
            "max-sm:static max-sm:m-0 max-sm:border-0 max-sm:shadow-none max-sm:pl-4":
              (side === "right" || side === "left") && !window.isApp,
            "left-full ml-1": side === "right" && !window.isApp,
            "right-full mr-1": side === "left" && !window.isApp,
            "left-4 top-0": side === "bottom" && !window.isApp,
            "group-hover/level-1:block": level === 1,
            "group-hover/level-2:block": level === 2,
            "pl-2.5": window.isApp && level === 1,
            "pl-5": window.isApp && level === 2,
          })}
        >
          {children}
        </ul>
      </li>
      {dividerBottom && <li className="divider" />}
    </>
  );
}

type DropdownToggleArgs = {
  title?: string;
  active?: boolean;
  disabled?: boolean;
  small?: boolean;
  icon?: IconProp;
  className?: string;
  iconClassName?: string;
  iconSize?: SizeProp;
  buttonType?: "light" | "primary" | "danger" | "normal" | "link";
  children?: React.ReactNode;
  showIcon?: boolean;
  ariaLabel?: string;
  dataTestid?: string;
  onToggle?: () => void;
};

export function DropDownToggle({
  active = false,
  disabled = false,
  small = false,
  showIcon = true,
  title,
  icon,
  className,
  buttonType = "light",
  iconClassName,
  children,
  iconSize,
  ariaLabel,
  dataTestid,
  onToggle,
}: DropdownToggleArgs) {
  function toggleDropdown(e) {
    e.stopPropagation();

    closeAllDropdowns();

    if (window.isApp && onToggle) {
      onToggle();
    } else {
      const currentDropdown = e.currentTarget.nextElementSibling;
      if (
        currentDropdown &&
        currentDropdown.classList.contains("tixxt__dropdown")
      ) {
        currentDropdown.classList.add("show");
      }
    }
  }

  return (
    <button
      // Without an explicit type a button defaults to type="submit" and
      // silently submits any surrounding form when the dropdown is opened
      type="button"
      className={classNames("btn dropdown-toggle space-x-1", className, {
        active,
        "btn-light": buttonType === "light",
        "btn-primary": buttonType === "primary",
        "btn-danger": buttonType === "danger",
        "btn-link": buttonType === "link",
        "btn-normal": buttonType === "normal",
        "btn-sm": small,
        "btn-mobile": window.isApp,
      })}
      data-bs-toggle="dropdown"
      data-testid={dataTestid}
      title={title}
      disabled={disabled}
      onClick={toggleDropdown}
      role="button"
      aria-label={ariaLabel}
      aria-haspopup="menu"
    >
      {showIcon && (
        <FontAwesomeIcon
          className={classNames(iconClassName, {
            "mr-1.5": Children.count(children) > 0,
          })}
          icon={icon || faEllipsis}
          size={iconSize}
        />
      )}
      {children}
    </button>
  );
}

/**
 * Wrapper for DropDown that handles mobile BottomSheet behavior.
 *
 * @param showToggleButton - When false, the DropDownToggle is not rendered.
 *   This only works because on non-mobile (!window.isApp), the wrapper returns
 *   children directly without modification. On mobile, hiding the toggle is useful
 *   when the dropdown is triggered by other means (e.g., long-press).
 * @param showDropDown - External control for opening the sheet (e.g., from long-press)
 * @param onClose - Callback when sheet closes (e.g., backdrop tap, swipe)
 */
export function DropDownWrapper({
  showDropDown,
  onClose,
  showToggleButton = true,
  children,
}: {
  children: React.ReactNode;
  showDropDown?: boolean;
  showToggleButton?: boolean;
  onClose?: () => void;
}) {
  const { showSheet, isAnimating, toggleSheet, closeSheet, openSheet } =
    useBottomSheet();

  useEffect(() => {
    if (showDropDown) openSheet();
  }, [showDropDown, openSheet]);

  useEffect(() => {
    if (!showSheet) onClose?.();
  }, [showSheet, onClose]);

  // On non-mobile, render children directly - DropDown handles its own visibility
  if (!window.isApp) return <>{children}</>;

  return (
    <>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement<DropdownToggleArgs | DropDownArgs>(child))
          return child;

        if (child.type === DropDownToggle) {
          return (
            showToggleButton &&
            React.cloneElement(child, {
              onToggle: toggleSheet,
            })
          );
        }

        if (child.type === DropDown) {
          return (
            <BottomSheet
              showSheet={showSheet}
              isAnimating={isAnimating}
              onClose={closeSheet}
            >
              <DropdownContext.Provider
                value={{
                  onItemSelect: closeSheet,
                }}
              >
                {React.cloneElement(child)}
              </DropdownContext.Provider>
            </BottomSheet>
          );
        }

        return child;
      })}
    </>
  );
}

DropDown.Item = DropDownItem;
DropDown.SubMenu = DropDownSubMenu;
DropDown.SubSubMenu = DropDownSubSubMenu;
DropDown.Toggle = DropDownToggle;
DropDown.Wrapper = DropDownWrapper;

export default DropDown;
